import React, { useEffect, useMemo, useState } from 'react';

import { Button, Text } from '@shared/components/bootstrap';

import { Panel } from '@admin/components/helpers/panel';
import { usePermission } from '@admin/hooks/use_permission';
import { ACCESS_CONTROL_PERMISSIONS_ADMIN } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import {
  AccessControlPermission,
  AccessControlPermissionsAdminQuery,
  AccessControlRoleAdmin,
  Status,
  useAccessControlRolePermissionsUpdateMutation,
} from '@admin/schema';

import {
  ActionTitle,
  Actions,
  Badge,
  EmptyState,
  ExplorerPermissionItem,
  ExplorerPermissionList,
  ExplorerResourceGroup,
  ExplorerResourceHeader,
  ExplorerResourceMeta,
  ExplorerSidebar,
  Header,
  Layout,
  PermissionCard,
  PermissionDescription,
  PermissionTitle,
  RoleCheckboxGrid,
  ScrollableContent,
  SearchInput,
  Section,
  SectionTitle,
  SelectionMeta,
  SelectionTitle,
  Subtitle,
  Title,
  Toolbar,
  UserAccessEmail,
  UserAccessInfo,
  UserAccessName,
  UserAccessRow,
  Chip,
  ChipList,
  DetailMeta,
  formatRoleName,
  groupPermissions,
  matchesQuery,
  sortPermissions,
} from './permissions_admin_shared';
import {
  permissionLabel,
  roleGrantCountByPermissionId,
  rolesWithPermission,
  usersWithPermission,
} from './permissions_admin_utils';

type SelectedPermission = Pick<AccessControlPermission, 'id' | 'resource' | 'action' | 'description' | 'key'>;

type AccessControlPermissionsAdminExplorerProps = {
  data: AccessControlPermissionsAdminQuery;
  onSave: () => void;
};

export const AccessControlPermissionsAdminExplorer: React.FC<AccessControlPermissionsAdminExplorerProps> = ({
  data,
  onSave,
}) => {
  const admin = data.accessControlPermissionsAdmin;
  const permissions = admin.permissions;
  const roles = admin.roles;
  const users = admin.staffUsers;

  const canUpdate = usePermission(
    ACCESS_CONTROL_PERMISSIONS_ADMIN.resource,
    ACCESS_CONTROL_PERMISSIONS_ADMIN.actions.update,
  );
  const [execute, { loading }] = useAccessControlRolePermissionsUpdateMutation({ client });

  const [search, setSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [selectedPermission, setSelectedPermission] = useState<SelectedPermission | null>(null);
  const [expandedResources, setExpandedResources] = useState<Record<string, boolean>>({});
  const [draftGrantingRoleIds, setDraftGrantingRoleIds] = useState<Set<string>>(new Set());
  const [saveError, setSaveError] = useState<string | null>(null);

  const roleGrantCounts = useMemo(() => roleGrantCountByPermissionId(roles), [roles]);

  const permissionGroups = useMemo(() => {
    const grouped = groupPermissions(permissions);
    return Object.entries(grouped)
      .filter(([resource, resourcePermissions]) =>
        resourcePermissions.some((permission) =>
          matchesQuery(search, [
            resource,
            permission.action,
            permission.description ?? '',
            permission.key,
            permissionLabel(permission),
          ]),
        ),
      )
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([resource, resourcePermissions]) => {
        const filteredPermissions = sortPermissions(
          resourcePermissions.filter((permission) =>
            matchesQuery(search, [
              resource,
              permission.action,
              permission.description ?? '',
              permission.key,
              permissionLabel(permission),
            ]),
          ),
        );

        return [resource, filteredPermissions] as const;
      });
  }, [permissions, search]);

  const grantingRoles = useMemo(
    () => (selectedPermission ? rolesWithPermission(selectedPermission.id, roles) : []),
    [roles, selectedPermission],
  );

  const serverGrantingRoleIdsKey = useMemo(
    () =>
      grantingRoles
        .map((role) => role.id)
        .sort()
        .join(','),
    [grantingRoles],
  );

  useEffect(() => {
    setDraftGrantingRoleIds(new Set(grantingRoles.map((role) => role.id)));
    setSaveError(null);
  }, [selectedPermission?.id, serverGrantingRoleIdsKey, grantingRoles]);

  const hasRoleGrantChanges = useMemo(() => {
    if (!selectedPermission) return false;

    const serverIds = new Set(grantingRoles.map((role) => role.id));
    if (draftGrantingRoleIds.size !== serverIds.size) return true;

    return Array.from(draftGrantingRoleIds).some((roleId) => !serverIds.has(roleId));
  }, [draftGrantingRoleIds, grantingRoles, selectedPermission]);

  const allGrantingUsers = useMemo(() => {
    if (!selectedPermission) return [];

    return usersWithPermission(selectedPermission.id, roles, users);
  }, [roles, selectedPermission, users]);

  const filteredGrantingUsers = useMemo(() => {
    return allGrantingUsers.filter(({ user, grantingRoles: userRoles }) =>
      matchesQuery(userSearch, [
        user.name,
        user.email,
        user.roles.join(' '),
        userRoles.map((role) => formatRoleName(role.name)).join(' '),
      ]),
    );
  }, [allGrantingUsers, userSearch]);

  const toggleResource = (resource: string) => {
    setExpandedResources((current) => ({ ...current, [resource]: !current[resource] }));
  };

  const selectPermission = (permission: SelectedPermission) => {
    setSelectedPermission(permission);
    setExpandedResources((current) => ({ ...current, [permission.resource]: true }));
    setUserSearch('');
  };

  const toggleRoleGrant = (roleId: string) => {
    setDraftGrantingRoleIds((current) => {
      const next = new Set(current);
      if (next.has(roleId)) {
        next.delete(roleId);
      } else {
        next.add(roleId);
      }
      return next;
    });
  };

  const saveRoleGrants = async () => {
    if (!selectedPermission || !canUpdate) return;

    const changedRoles = roles.filter((role) => {
      const wasGranted = role.permissionIds.includes(selectedPermission.id);
      const shouldGrant = draftGrantingRoleIds.has(role.id);
      return wasGranted !== shouldGrant;
    });

    if (changedRoles.length === 0) return;

    setSaveError(null);
    try {
      for (const role of changedRoles) {
        const permissionIds = draftGrantingRoleIds.has(role.id)
          ? Array.from(new Set([...role.permissionIds, selectedPermission.id]))
          : role.permissionIds.filter((id) => id !== selectedPermission.id);

        const { data: mutationData } = await execute({
          variables: { roleId: role.id, permissionIds },
        });
        const result = mutationData?.accessControlRolePermissionsUpdate;
        if (result?.status !== Status.Ok) {
          setSaveError(result?.error ?? `Failed to update ${formatRoleName(role.name)}.`);
          return;
        }
      }

      onSave();
    } catch (mutationError) {
      setSaveError(mutationError instanceof Error ? mutationError.message : 'Failed to save role grants.');
    }
  };

  const expandAll = () => {
    setExpandedResources(Object.fromEntries(permissionGroups.map(([resource]) => [resource, true])));
  };

  const collapseAll = () => {
    setExpandedResources(Object.fromEntries(permissionGroups.map(([resource]) => [resource, false])));
  };

  const roleSummary = (role: AccessControlRoleAdmin) => {
    const viaJobRoleCount = users.filter((user) => user.roles.includes(role.name)).length;
    const explicitCount = role.assignedUserCount;
    if (viaJobRoleCount > 0 && explicitCount > 0) {
      return `${viaJobRoleCount} via job role · ${explicitCount} explicit`;
    }
    if (viaJobRoleCount > 0) return `${viaJobRoleCount} via job role`;
    if (explicitCount > 0) return `${explicitCount} explicit`;
    return 'No assigned users';
  };

  return (
    <>
      <Toolbar>
        <SearchInput
          aria-label="Search permissions"
          placeholder="Search by resource, action, or description"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <button type="button" className="btn btn-default btn-sm" onClick={expandAll}>
          Expand all
        </button>
        <button type="button" className="btn btn-default btn-sm" onClick={collapseAll}>
          Collapse all
        </button>
      </Toolbar>

      <Layout>
        <ExplorerSidebar>
          {permissionGroups.length === 0 ? (
            <EmptyState>No permissions match your search.</EmptyState>
          ) : (
            permissionGroups.map(([resource, resourcePermissions]) => {
              const expanded = Boolean(expandedResources[resource]);

              return (
                <ExplorerResourceGroup key={resource}>
                  <ExplorerResourceHeader
                    type="button"
                    expanded={expanded}
                    aria-expanded={expanded}
                    onClick={() => toggleResource(resource)}
                  >
                    <span>{resource}</span>
                    <ExplorerResourceMeta>
                      {resourcePermissions.length} · {expanded ? '−' : '+'}
                    </ExplorerResourceMeta>
                  </ExplorerResourceHeader>
                  {expanded && (
                    <ExplorerPermissionList>
                      {resourcePermissions.map((permission) => {
                        const roleCount = roleGrantCounts.get(permission.id) ?? 0;

                        return (
                          <ExplorerPermissionItem
                            key={permission.id}
                            type="button"
                            active={permission.id === selectedPermission?.id}
                            onClick={() => selectPermission(permission)}
                          >
                            <SelectionTitle>
                              <ActionTitle as="span">{permission.action}</ActionTitle>
                            </SelectionTitle>
                            <SelectionMeta>
                              <Badge tone={roleCount === 0 ? 'warning' : 'accent'}>{roleCount} roles</Badge>
                            </SelectionMeta>
                          </ExplorerPermissionItem>
                        );
                      })}
                    </ExplorerPermissionList>
                  )}
                </ExplorerResourceGroup>
              );
            })
          )}
        </ExplorerSidebar>

        <Panel>
          <Panel.Body>
            {selectedPermission ? (
              <>
                <Header>
                  <Title style={{ fontSize: 22 }}>{permissionLabel(selectedPermission)}</Title>
                  <Subtitle>{selectedPermission.description || selectedPermission.key}</Subtitle>
                  <DetailMeta>
                    <Badge tone={grantingRoles.length === 0 ? 'warning' : 'accent'}>
                      {grantingRoles.length} granting roles
                    </Badge>
                    <Badge>{allGrantingUsers.length} users with access</Badge>
                  </DetailMeta>
                </Header>

                <ScrollableContent>
                  <Section>
                    <SectionTitle>Roles that grant this permission</SectionTitle>
                    <RoleCheckboxGrid>
                      {roles.map((role) => (
                        <PermissionCard key={role.id}>
                          <input
                            type="checkbox"
                            checked={draftGrantingRoleIds.has(role.id)}
                            disabled={!canUpdate}
                            onChange={() => toggleRoleGrant(role.id)}
                          />
                          <span>
                            <PermissionTitle>{formatRoleName(role.name)}</PermissionTitle>
                            <PermissionDescription>{roleSummary(role)}</PermissionDescription>
                          </span>
                        </PermissionCard>
                      ))}
                    </RoleCheckboxGrid>

                    {saveError && <Text style="danger">{saveError}</Text>}

                    {canUpdate && hasRoleGrantChanges && (
                      <Actions>
                        <Button kind="primary" loading={loading} onClick={saveRoleGrants}>
                          Save role grants
                        </Button>
                      </Actions>
                    )}
                  </Section>

                  <Section>
                    <SectionTitle>Users with access</SectionTitle>
                    <SearchInput
                      aria-label="Filter users with access"
                      placeholder="Filter users by name, email, or role"
                      value={userSearch}
                      onChange={(event) => setUserSearch(event.target.value)}
                      style={{ marginBottom: 12, maxWidth: '100%' }}
                    />
                    {allGrantingUsers.length === 0 ? (
                      <EmptyState style={{ padding: '16px 0' }}>
                        {users.length === 0 ? 'No active users found.' : 'No users currently have this permission.'}
                      </EmptyState>
                    ) : filteredGrantingUsers.length === 0 ? (
                      <EmptyState style={{ padding: '16px 0' }}>No users match this filter.</EmptyState>
                    ) : (
                      filteredGrantingUsers.map(({ user, grantingRoles: userRoles }) => (
                        <UserAccessRow key={user.id}>
                          <UserAccessInfo>
                            <UserAccessName>{user.name}</UserAccessName>
                            <UserAccessEmail>{user.email}</UserAccessEmail>
                          </UserAccessInfo>
                          <ChipList>
                            {userRoles.map((role) => (
                              <Chip key={role.id}>{formatRoleName(role.name)}</Chip>
                            ))}
                          </ChipList>
                        </UserAccessRow>
                      ))
                    )}
                  </Section>
                </ScrollableContent>
              </>
            ) : (
              <EmptyState>Select a permission to view access and edit which roles grant it.</EmptyState>
            )}
          </Panel.Body>
        </Panel>
      </Layout>
    </>
  );
};

export const PermissionExplorerPanel = AccessControlPermissionsAdminExplorer;
